<template>
  <v-container fluid v-if="getPermission('setting:view')">
    <v-layout class="pb-4">
      <v-flex>
        <h3
          class="font-weight-700 mb-0 font-size-24 custom-headline color-custom-blue"
        >
          Branding
        </h3>
      </v-flex>
      <v-flex class="text-right">
        <v-menu offset-y left>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top content-class="custom-top-tooltip">
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  depressed
                  :disabled="pageLoading"
                  class="mr-4 custom-bold-button"
                  color="cyan white--text"
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                >
                  {{ entity.name }}
                </v-btn>
              </template>
              <span>Entity</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in entityList"
              :key="index"
              link
              v-on:click="updateEntity(item)"
            >
              <v-list-item-title class="font-weight-600">{{
                item.name
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          v-on:click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </v-flex>
    </v-layout>

    <perfect-scrollbar
      :options="{ suppressScrollX: true }"
      class="scroll custom-box-top-inner-shadow"
      style="height: calc(100vh - 150px); position: relative"
    >
      <!-- Section 1: Company Logo -->
      <v-card flat outlined class="rounded-lg mb-5 overflow-hidden">
        <v-card-title
          class="grey lighten-4 py-3 px-4 border-bottom d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-icon color="cyan darken-1" class="mr-2"
              >mdi-image-filter-center-focus</v-icon
            >
            <h3
              class="subtitle-1 font-weight-bold grey--text text--darken-3 mb-0"
            >
              Company Logo
            </h3>
          </div>
          <v-chip
            x-small
            color="cyan lighten-5"
            class="cyan--text text--darken-2 font-weight-bold px-3"
          >
            Standard dimension: 450 × 450 px
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-row class="align-center">
            <!-- Current Logo Preview Card -->
            <v-col cols="12" md="6">
              <v-card
                outlined
                class="pa-4 d-flex flex-column align-center justify-center rounded-lg grey lighten-5 border-dashed"
                style="min-height: 280px; position: relative"
              >
                <div
                  class="caption-1 font-weight-bold grey--text text--darken-1 mb-3 align-self-start"
                >
                  <v-icon small class="mr-1" color="cyan"
                    >mdi-eye-outline</v-icon
                  >
                  Active Logo Preview
                </div>

                <div
                  class="logo-img-box d-flex align-center justify-center width-100 pa-2 bg-white rounded shadow-sm border mb-3"
                  style="width: 100%; max-width: 320px; height: 180px"
                >
                  <v-img
                    v-if="detail.company_logo"
                    contain
                    max-width="300"
                    max-height="160"
                    lazy-src="https://fakeimg.pl/450x450"
                    :src="detail.company_logo"
                  ></v-img>
                  <v-img
                    v-else
                    contain
                    max-width="300"
                    max-height="160"
                    lazy-src="https://fakeimg.pl/450x450"
                    src="https://fakeimg.pl/450x450"
                  ></v-img>
                </div>

                <template v-if="getPermission('setting:update')">
                  <v-btn
                    depressed
                    v-on:click="changeImage"
                    small
                    color="cyan"
                    dark
                    class="custom-bold-button text-capitalize px-4"
                    :disabled="pageLoading"
                    :loading="pageLoading"
                  >
                    <v-icon left small>mdi-cloud-upload-outline</v-icon>
                    Upload / Change Logo
                  </v-btn>
                  <div class="d-none">
                    <v-file-input
                      ref="uploadImage"
                      v-on:change="uploadCompanyLogo"
                      :rules="imageRules"
                      accept="image/png, image/jpeg, image/jpg"
                    ></v-file-input>
                  </div>
                </template>
              </v-card>
            </v-col>

            <!-- Guide / Sample Card -->
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4 rounded-lg bg-white">
                <div
                  class="caption-1 font-weight-bold grey--text text--darken-1 mb-2"
                >
                  <v-icon small class="mr-1" color="cyan"
                    >mdi-information-outline</v-icon
                  >
                  Logo Placement Reference
                </div>
                <div
                  class="d-flex align-center justify-center pa-2 grey lighten-4 rounded border"
                >
                  <v-img
                    contain
                    max-width="320"
                    max-height="200"
                    lazy-src="https://fakeimg.pl/450x450"
                    src="https://bthrust-fsm.s3.ap-southeast-1.amazonaws.com/ATYDJHIO545/files/image/png/04oTO4w2gBNdv1fG33pmz9CAIrhuKULDdzdIzbmO.png"
                  ></v-img>
                </div>
                <div
                  class="caption-1 grey--text text--darken-1 mt-2 text-center"
                >
                  Recommended transparent PNG or JPG under 2MB.
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Section 2: PDF Header & Footer -->
      <v-card flat outlined class="rounded-lg overflow-hidden">
        <v-card-title
          class="grey lighten-4 py-3 px-4 border-bottom d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-icon color="cyan darken-1" class="mr-2">mdi-file-pdf-box</v-icon>
            <h3
              class="subtitle-1 font-weight-bold grey--text text--darken-3 mb-0"
            >
              PDF Header &amp; Footer Templates
            </h3>
          </div>
          <v-chip
            x-small
            color="cyan lighten-5"
            class="cyan--text text--darken-2 font-weight-bold px-3"
          >
            Standard dimension: 1644 × 287 px
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-row>
            <v-col
              cols="12"
              v-for="item in items"
              :key="item.title"
              class="mb-4"
            >
              <v-card outlined class="pa-4 rounded-lg grey lighten-5">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div
                    class="subtitle-2 font-weight-bold grey--text text--darken-3 d-flex align-center"
                  >
                    <v-icon small color="cyan" class="mr-2"
                      >mdi-page-layout-header</v-icon
                    >
                    {{ item.title }}
                  </div>
                  <template v-if="getPermission('setting:update')">
                    <v-btn
                      depressed
                      v-on:click="changeHeaderFooter(item.key)"
                      small
                      color="cyan"
                      dark
                      class="custom-bold-button text-capitalize px-3"
                      :disabled="pageLoading"
                      :loading="pageLoading"
                    >
                      <v-icon left small>mdi-cloud-upload-outline</v-icon>
                      Upload Banner
                    </v-btn>
                    <div class="d-none">
                      <v-file-input
                        :ref="item.key"
                        v-on:change="uploadHeader($event, item.key)"
                        :rules="imageRules"
                        accept="image/png, image/jpeg, image/jpg"
                      ></v-file-input>
                    </div>
                  </template>
                </div>

                <div
                  class="d-flex align-center justify-center bg-white rounded border pa-2 overflow-hidden"
                  style="min-height: 120px"
                >
                  <v-img
                    v-if="headerFooter[item.key]"
                    contain
                    max-width="745"
                    max-height="135"
                    lazy-src="https://fakeimg.pl/745x135"
                    :src="headerFooter[item.key]"
                  ></v-img>
                  <v-img
                    v-else
                    contain
                    max-width="745"
                    max-height="135"
                    lazy-src="https://fakeimg.pl/745x135"
                    src="https://fakeimg.pl/745x135"
                  ></v-img>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </perfect-scrollbar>
  </v-container>
</template>

<script>
import { UPLOAD, PATCH, QUERY } from "@/core/services/store/request.module";
import { getConfig } from "@/core/services/local.service";
export default {
  name: "profile-branding-setting",
  data() {
    return {
      pageLoading: true,
      items: [
        {
          title: "Quotation Header",
          key: "quotation_header",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg",
        },
        /*{
          title: "Quotation Footer",
          key: "quotation_footer",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg"
        },*/
        {
          title: "Invoice Header",
          key: "invoice_header",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg",
        }, 
        /*{
          title: "Invoice Footer",
          key: "invoice_footer",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg"
        },*/
        /*   {
          title: "Payment Header",
          key: "payment_header",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg",
        }, */
        /*{
          title: "Payment Footer",
          key: "payment_footer",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg"
        },*/
        {
          title: "Visit Header",
          key: "visit_header",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg",
        },
        /*  {
          title: "Delivery Header",
          key: "delivery_header",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg",
        },*/
        {
          title: "Project Header",
          key: "project_header",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg",
        },
        /*{
          title: "Project Costing Footer",
          key: "project_costing_footer",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg"
        },*/
        /*{
          title: "Purchase Order Header",
          key: "purchase_header",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg",
        },*/
        /*{
          title: "Purchase Order Footer",
          key: "purchase_footer",
          avatar: "https://cdn.vuetifyjs.com/images/lists/1.jpg"
        }*/
      ],
      headerFooter: {},
      detail: {
        company_logo: null,
        facebook_url: null,
        twitter_url: null,
        instagram_url: null,
        google_plus_url: null,
      },
      entityList: [],
      entity: {},
      imageRules: [
        (value) =>
          !value ||
          value.size < 2000000 ||
          "Logo size should be less than 2 MB!",
      ],
    };
  },
  methods: {
    getBrandingSetting() {
      const _this = this;
      _this.detail = new Object({
        company_logo: null,
        facebook_url: null,
        twitter_url: null,
        instagram_url: null,
        google_plus_url: null,
      });
      _this.$store
        .dispatch(QUERY, {
          url: "setting/branding",
          data: { entity: this.entity.id },
        })
        .then(({ data }) => {
          _this.detail = data;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    getHeaderFooterSetting() {
      const _this = this;
      _this.headerFooter = new Object();
      _this.$store
        .dispatch(QUERY, {
          url: "setting/header-footer",
          data: { entity: this.entity.id },
        })
        .then(({ data }) => {
          _this.headerFooter = data;
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    uploadCompanyLogo(param) {
      const _this = this;
      let formData = new FormData();
      _this.pageLoading = true;
      formData.append("files[]", param, param.name);
      _this.$store
        .dispatch(UPLOAD, { url: "file-manager/upload", data: formData })
        .then(async ({ data }) => {
          let uploaded_id = 0;
          for (let i = data.length - 1; i >= 0; i--) {
            _this.detail.company_logo = data[i].file.url;
            uploaded_id = data[i]?.id;
            break;
          }
          await _this.updateCompanyLogo(uploaded_id);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    uploadHeader(param, key) {
      const _this = this;
      let formData = new FormData();
      _this.pageLoading = true;
      formData.append("files[]", param, param.name);
      _this.$store
        .dispatch(UPLOAD, { url: "file-manager/upload", data: formData })
        .then(async ({ data }) => {
          let uploaded_id = 0;
          for (let i = data.length - 1; i >= 0; i--) {
            uploaded_id = data[i]?.id;
            _this.detail[key] = data[i].file.url;
            break;
          }
          await _this.updateCompanyHeader(key, uploaded_id);
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    updateCompanyLogo(uploaded_id = 0) {
      const _this = this;
      return new Promise((resolve, reject) => {
        _this.$store
          .dispatch(PATCH, {
            url: "setting/branding",
            data: {
              company_logo: _this.detail.company_logo,
              entity: _this.entity.id,
              uploaded_id: uploaded_id,
            },
          })
          .then(() => {
            resolve(true);
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            _this.getBrandingSetting();
          });
      });
    },
    updateCompanyHeader(key, id = 0) {
      const _this = this;
      return new Promise((resolve, reject) => {
        _this.$store
          .dispatch(PATCH, {
            url: "setting/header-footer",
            data: {
              image: _this.detail[key],
              key,
              entity: _this.entity.id,
              uploaded_id: id,
            },
          })
          .then(() => {
            resolve(true);
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            _this.getHeaderFooterSetting();
          });
      });
    },

    updateBrandingSetting() {
      const _this = this;
      _this.pageLoading = true;
      _this.$store
        .dispatch(PATCH, {
          url: "setting/branding",
          data: { ..._this.detail, entity: _this.entity.id },
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.pageLoading = false;
        });
    },
    changeImage() {
      this.$refs["uploadImage"].$refs["input"].click();
    },
    changeHeaderFooter(key) {
      if (
        this.lodash.isEmpty(this.$refs[key]) === false &&
        this.lodash.isEmpty(this.$refs[key][0]) === false
      ) {
        this.$refs[key][0].$refs["input"].click();
      }
    },
    updateEntity(row) {
      this.entity = row;
      this.pageLoading = true;
      this.getBrandingSetting();
      this.getHeaderFooterSetting();
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.entity) {
        this.updateEntity(this.entity);
      }
    });
  },
  beforeMount() {
    this.entityList = getConfig("entity");
    this.$nextTick(() => {
      this.entity = this.lodash.find(this.entityList, { id: 1 });
    });
  },
};
</script>
<style scoped>
span.changestyle {
  font-size: 12px;
  text-transform: capitalize;
  color: black;
}
</style>
